import { FaRegSquare, FaSquareCheck } from "react-icons/fa6"
import { LuInfo, LuMinus, LuPlus } from "react-icons/lu"
import { LuChevronLeft, LuMoveLeft } from "react-icons/lu"
import { useLocation, useNavigate } from "react-router-dom"
import { toast } from "react-toastify"

import SolidBtn from "@fuse/sharedComponents/buttons/SolidBtn"
import GenericFeedback from "@fuse/sharedComponents/GenericFeedback"
import { useSignUpModalState } from "@fuse/sharedComponents/globalQuickSignUpModal"
import BlurredLoadingSpinner from "@fuse/sharedComponents/loader/BlurredLoadingSpinner"
import {
  showErrorToast,
  showInfoToast,
} from "@fuse/sharedComponents/themeToast/toastUtils"
import FancyText from "@fuse/sharedComponents/typography/FancyText"
import { isEmpty } from "lodash-es"
import { useRecoilValue } from "recoil"
import { ROUTES } from "src/App"

import { userStateForAnalytics } from "@fuse/recoil/signup"

import FastSignUpComponent from "@fuse/pages/eventPage/FastSignUpComponent"
import { useChooseTicket } from "@fuse/pages/eventSignUp/BuyTickets/ChooseTicket/useChooseTicket"
import BuyTicketsLayout from "@fuse/pages/eventSignUp/BuyTickets/components/BuyTicketsLayout"
import { getTicketWithServiceFees } from "@fuse/pages/eventSignUp/eventHelper"

interface TicketCardProps {
  title: string
  price: number
  description: string
  requireApproval?: boolean // Optional badge for specific tickets
  quantity?: number
  isBuyForGroup?: boolean
  onClick: () => void
  handleAddQuantity: () => void
  handleSubtractQuantity: () => void
  onSingleGroupTicket: () => void
  totalSelectedQuantity?: number
}

const TicketCard: React.FC<TicketCardProps> = ({
  title,
  price,
  description,
  requireApproval,
  quantity,
  isBuyForGroup,
  onClick,
  handleAddQuantity,
  handleSubtractQuantity,
  onSingleGroupTicket,
  totalSelectedQuantity = 0,
}) => {
  // Calculate price with fees for this ticket
  const { payingPrice } = getTicketWithServiceFees(price)

  // Determine if this ticket should be grayed out
  // If totalSelectedQuantity > quantity, it means other  ticket types have been selected
  const shouldGrayOut = !isBuyForGroup && totalSelectedQuantity > quantity

  const handlePlusClick = (e: React.MouseEvent) => {
    e.stopPropagation()

    if (shouldGrayOut) {
      showInfoToast("You can only select 1 ticket type for this event")
      return
    }

    if (isBuyForGroup) {
      handleAddQuantity()
    } else {
      if (quantity === 0) {
        handleAddQuantity()
      } else {
        showInfoToast("You can only purchase 1 ticket for this event")
      }
    }
  }

  const handleCardClick = () => {
    if (shouldGrayOut) {
      showInfoToast("You can only select 1 ticket type for this event")
      return
    }
    onSingleGroupTicket()
  }
  return (
    <div
      className={`bg-white/10 rounded-lg p-4 shadow-md cursor-pointer ${
        shouldGrayOut ? "opacity-50" : ""
      }`}
      onClick={handleCardClick}
    >
      <div className="flex justify-between items-center">
        <h3 className="text-white text-lg font-semibold">{title}</h3>
        {/* {isBuyForGroup && ( */}
        <div className="flex flex-row items-center justify-center">
          <div
            className={`h-7 w-7 rounded-full bg-white flex items-center justify-center cursor-pointer ${
              shouldGrayOut ? "opacity-30" : ""
            }`}
            onClick={(e) => {
              e.stopPropagation()
              handleSubtractQuantity()
            }}
          >
            <LuMinus className="text-black" />
          </div>
          <p className="text-white text-sm mx-2">{quantity}</p>
          <div
            className={`h-7 w-7 rounded-full bg-white flex items-center justify-center cursor-pointer ${
              (quantity === 1 && !isBuyForGroup) || shouldGrayOut
                ? "opacity-30"
                : ""
            }`}
            onClick={handlePlusClick}
          >
            <LuPlus className="text-black" />
          </div>
        </div>
        {/* )} */}
      </div>
      <div className="h-[0.5px] self-center bg-white/20 my-2"></div>
      <div className="flex flex-col items-start">
        <p className="text-white mt-2 text-md font-medium">
          ${payingPrice.toFixed(2)}
        </p>
        <p className="text-white/30 text-md mt-1 text-start">{description}</p>
      </div>

      {requireApproval && (
        <div className="flex flex-col items-start mt-2">
          <div className="text-xs bg-white/15 text-white px-2 py-0.5 rounded-sm">
            Requires approval
          </div>
        </div>
      )}
      {/* {requireApproval && isBuyForGroup && (
        <div className="flex flex-col items-start mt-2">
          <div className="text-xs bg-white/15 text-white px-2 py-0.5 rounded-sm">
            Requires approval
          </div>
        </div>
      )} */}
    </div>
  )
}

const EventSignUp = () => {
  const navigate = useNavigate()
  const { state } = useLocation()
  const invitedId = state?.invitedId
  const user = useRecoilValue(userStateForAnalytics)
  const userId = user?.userId

  const {
    eventId,
    isLoading,
    eventsTickets,
    isBuyForGroup,
    setIsBuyForGroup,
    onSelectTicket,
    onSelectGroupTicket,
    flyerImage,
    eventName,
    filteredTickets,
    selectedDonateAmount,
    setSelectedDonateAmount,
    donations,
    handleAddQuantity,
    handleSubtractQuantity,
    onSingleGroupTicket,
    ticketsWithTotalPrice,
  } = useChooseTicket(invitedId, "chooseTicket")

  // Calculate total selected quantity across all tickets
  const totalSelectedQuantity =
    eventsTickets?.reduce(
      (total, ticket) => total + (ticket?.quantity || 0),
      0,
    ) || 0

  // Calculate total price with fees for continue button
  const calculateTotalWithFees = () => {
    if (!filteredTickets || filteredTickets.length === 0) return 0

    const ticketTotal = filteredTickets.reduce((total, ticket) => {
      return total + ticket.ticketPrice * ticket.quantity
    }, 0)

    if (ticketTotal === 0) return 0

    const { payingPrice } = getTicketWithServiceFees(ticketTotal)
    return payingPrice
  }

  const totalWithFees = calculateTotalWithFees()

  const handleBackPress = () => {
    if (!invitedId) navigate(ROUTES.EVENTS.replace(":eventId", eventId))
    else
      navigate(
        ROUTES.EVENTS.replace(":eventId", `${eventId}?invitedId=${invitedId}`),
      )
  }

  // Header component
  const HeaderComponent = () => (
    <div className="p-2 md:p-10">
      <div className="left-4 md:top-12 md:left-12 flex items-center space-x-2 z-20">
        <div
          onClick={handleBackPress}
          className="w-12 h-12 md:w-14 md:h-14 lg:w-16 lg:h-16 rounded-full flex justify-center items-center bg-white/10 cursor-pointer"
        >
          <LuChevronLeft className="text-white text-2xl mr-0.5 md:hidden" />
          <LuMoveLeft className="text-white text-md md:text-2xl hidden md:flex" />
        </div>
      </div>
    </div>
  )

  // Content component
  const ContentComponent = () => (
    <div className="mb-32">
      {eventId && isLoading && <BlurredLoadingSpinner />}
      <div className="flex flex-col items-center justify-center md:justify-start px-4">
        <div className="mb-8 text-start w-full max-w-sm">
          <p className="text-white/30 text-left text-sm mt-10 md:mt-0 lg:mt-0 font-medium">
            STEP 1
          </p>
          <FancyText className="text-2.5xl mt-[10px] text-white font-semibold">
            Choose your tickets
          </FancyText>
        </div>

        <div className="space-y-4 w-full max-w-sm mb-8">
          {eventsTickets?.map((item, index) => {
            if (!item.isActive) return null
            return (
              <TicketCard
                key={index.toString()}
                title={item?.ticketName}
                price={item?.ticketPrice}
                description={item?.ticketDescriptions}
                requireApproval={item?.requireApproval}
                quantity={item?.quantity}
                isBuyForGroup={isBuyForGroup}
                onClick={() => !isBuyForGroup && onSelectTicket(item)}
                handleAddQuantity={() => handleAddQuantity(item?.id)}
                handleSubtractQuantity={() => handleSubtractQuantity(item?.id)}
                onSingleGroupTicket={() => onSingleGroupTicket(item?.id)}
                totalSelectedQuantity={totalSelectedQuantity}
              />
            )
          })}

          {donations?.length > 0 && (
            <div className="w-full max-w-sm mt-8 flex flex-col gap-4">
              <FancyText className="text-2.5xl text-start text-white font-[600]">
                Donate
              </FancyText>
              {donations?.map((donation) => (
                <div className="bg-white/10 rounded-lg p-4 shadow-md">
                  <h3 className="text-lg text-start font-semibold text-white mb-2">
                    {donation?.ticketName}
                  </h3>
                  <div className="h-[0.5px] self-center bg-strokeColor my-2"></div>
                  <p className="text-white/30 text-left text-md mb-4 leading-relaxed">
                    {donation?.ticketDescriptions}
                  </p>

                  <div className="flex justify-between gap-2 mb-4">
                    {[5, 15, 30, 50].map((amount) => (
                      <button
                        onClick={() =>
                          amount === selectedDonateAmount
                            ? setSelectedDonateAmount(0)
                            : setSelectedDonateAmount(amount)
                        }
                        className="bg-white/5 py-2 flex-1 rounded text-white font-medium"
                        style={{
                          backgroundColor:
                            selectedDonateAmount === amount
                              ? "rgba(255, 255, 255, 0.2)"
                              : " ",
                          borderColor:
                            selectedDonateAmount === amount
                              ? "rgba(255, 255, 255, 0.5)"
                              : " ",
                        }}
                      >
                        $ {amount}
                      </button>
                    ))}
                  </div>

                  <div className="flex items-center bg-white/10 rounded px-3">
                    <span className="text-white font-medium mr-1">$</span>
                    <input
                      onChange={(e) => {
                        const value = Number(e.target.value)
                        if (value >= 0) {
                          setSelectedDonateAmount(value)
                        }
                      }}
                      value={selectedDonateAmount}
                      className="bg-transparent pl-2 text-white w-full py-2 outline-none"
                      placeholder="Other"
                      type="number"
                      min="0"
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="mb-2">
          <GenericFeedback
            fromScreen="Choose Ticket"
            userId={userId}
            showOnDesktop={true}
          />
          <GenericFeedback fromScreen="Choose Ticket" userId={userId} />
        </div>
      </div>
    </div>
  )

  // Footer component
  const FooterComponent = () => (
    <div className="w-full py-4">
      <div className="flex items-center justify-center self-center">
        <button
          className={`px-6 lg:px-8 min-w-80 w-full max-w-sm mx-4 bg-white/10 rounded-full border ${isEmpty(filteredTickets) ? "text-white/50" : "text-white"}`}
          onClick={() =>
            isEmpty(filteredTickets)
              ? showInfoToast("Select a ticket to continue")
              : onSelectGroupTicket()
          }
        >
          {totalWithFees > 0
            ? `Continue - $${totalWithFees.toFixed(2)}`
            : "Continue"}
        </button>
      </div>
    </div>
  )

  const { setShowFastSignUpPopUp, showFastSignUpPopUp } = useSignUpModalState()

  return (
    <>
      <BuyTicketsLayout
        header={HeaderComponent()}
        content={ContentComponent()}
        footer={FooterComponent()}
        bgImage={flyerImage}
      />

      <FastSignUpComponent
        isOpen={showFastSignUpPopUp}
        setOpenState={setShowFastSignUpPopUp}
        title={eventName}
      />
    </>
  )
}

export default EventSignUp
